import api from './api'

export interface LoginCredentials {
  email: string
  password: string
}

export interface UserProfile {
  id: string
  email: string
  full_name: string | null
  avatar_url: string | null
  is_verified: boolean
  is_superadmin: boolean
  two_factor_enabled: boolean
  settings: {
    language?: string
    theme?: string
  }
}

export interface LoginResponse {
  access_token: string | null
  refresh_token: string | null
  token_type: string
  user: {
    id: string
    email: string
    full_name: string | null
    avatar_url: string | null
    is_verified: boolean
    is_superadmin: boolean
    two_factor_enabled: boolean
    settings: {
      language?: string
      theme?: string
    }
  } | null
  requires_2fa: boolean
  temporary_token: string | null
  device_token: string | null
}

export interface ProfileUpdateRequest {
  full_name?: string
  avatar_url?: string | null
  language?: string
  theme?: string
}

export interface ProfileUpdateResponse {
  message: string
  profile: UserProfile
}

export interface ChangePasswordRequest {
  current_password: string
  new_password: string
  new_password_confirm: string
}

export interface ApiError {
  detail: string
}

export interface MessageResponse {
  message: string
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/auth/login', credentials)
    return response.data
  },

  async refreshToken(refreshToken: string): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/auth/refresh', {
      refresh_token: refreshToken,
    })
    return response.data
  },

  async requestPasswordReset(email: string): Promise<MessageResponse> {
    const response = await api.post<MessageResponse>('/auth/request-password-reset', {
      email,
    })
    return response.data
  },

  async resetPassword(token: string, password: string, passwordConfirm: string): Promise<MessageResponse> {
    const response = await api.post<MessageResponse>('/auth/reset-password', {
      token,
      password,
      password_confirm: passwordConfirm,
    })
    return response.data
  },

  // 2FA Methods

  async verify2FACode(
    code: string,
    temporaryToken: string,
    rememberDevice: boolean = false,
    deviceFingerprint?: string
  ): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>(
      '/auth/verify-2fa-code',
      {
        code,
        temporary_token: temporaryToken,
        remember_device: rememberDevice,
      },
      {
        headers: deviceFingerprint
          ? { 'X-Device-Fingerprint': deviceFingerprint }
          : undefined,
      }
    )
    return response.data
  },

  async enable2FA(password: string): Promise<MessageResponse> {
    const response = await api.post<MessageResponse>('/auth/enable-2fa', {
      password,
    })
    return response.data
  },

  async disable2FA(password: string): Promise<MessageResponse> {
    const response = await api.post<MessageResponse>('/auth/disable-2fa', {
      password,
    })
    return response.data
  },

  async get2FAStatus(): Promise<{ enabled: boolean }> {
    const response = await api.get<{ enabled: boolean }>('/auth/2fa-status')
    return response.data
  },

  async resend2FACode(temporaryToken: string): Promise<{ temporary_token: string; message: string }> {
    const response = await api.post<{ temporary_token: string; message: string }>(
      '/auth/resend-2fa-code',
      { temporary_token: temporaryToken }
    )
    return response.data
  },

  // Profile Methods

  async getProfile(): Promise<UserProfile> {
    const response = await api.get<UserProfile>('/auth/me')
    return response.data
  },

  async updateProfile(data: ProfileUpdateRequest): Promise<ProfileUpdateResponse> {
    const response = await api.patch<ProfileUpdateResponse>('/auth/profile', data)
    return response.data
  },

  async changePassword(data: ChangePasswordRequest): Promise<MessageResponse> {
    const response = await api.post<MessageResponse>('/auth/change-password', data)
    return response.data
  },
}
